import ramblaIcon from 'assets/img/rambla-icon.png';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import { FiExternalLink } from 'react-icons/fi';
import {
  MdFullscreen,
  MdFullscreenExit,
  MdPause,
  MdPlayArrow,
  MdVolumeOff,
  MdVolumeUp,
} from 'react-icons/md';
import ReactPlayer from 'react-player';
import * as Ui from './VideoPlayer.styles';

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];
const HIDE_CONTROLS_DELAY = 2500;
const SEEK_STEP = 5;

const ZERO_WIDTH_STYLE = { width: '0%' };
const ZERO_LEFT_STYLE = { left: '0%' };

const initialState = {
  playing: false,
  muted: false,
  volume: 1,
  playbackRate: 1,
  isFullscreen: false,
  isLoading: true,
  hasError: false,
  showControls: true,
  showRateMenu: false,
  hasStarted: false,
};

const videoReducer = (state, action) => {
  switch (action.type) {
    case 'PLAY':
      if (state.hasError) return state;
      return { ...state, playing: true, showControls: true, hasStarted: true };
    case 'PAUSE':
      return { ...state, playing: false, showControls: true };
    case 'TOGGLE_PLAY':
      if (state.hasError) return state;
      return { ...state, playing: !state.playing, showControls: true, hasStarted: true };
    case 'SET_LOADING':
      if (state.hasError) return state;
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return {
        ...state,
        playing: false,
        isLoading: false,
        hasError: true,
        showControls: true,
        showRateMenu: false,
      };
    case 'SET_VOLUME':
      return { ...state, volume: action.payload, muted: action.payload === 0 };
    case 'TOGGLE_MUTED':
      return { ...state, muted: !state.muted };
    case 'SET_RATE':
      return { ...state, playbackRate: action.payload, showRateMenu: false };
    case 'TOGGLE_RATE_MENU':
      return { ...state, showRateMenu: !state.showRateMenu };
    case 'HIDE_RATE_MENU':
      return { ...state, showRateMenu: false };
    case 'SET_FULLSCREEN':
      return { ...state, isFullscreen: action.payload };
    case 'SHOW_CONTROLS':
      if (action.payload) {
        return state.showControls ? state : { ...state, showControls: true };
      }
      if (!state.showControls && !state.showRateMenu) return state;
      return { ...state, showControls: false, showRateMenu: false };
    case 'RESET_VIDEO':
      return { ...state, isLoading: true, hasError: false, hasStarted: false };
    default:
      return state;
  }
};

const MediaSurface = React.memo(
  React.forwardRef(
    (
      {
        src,
        playing,
        muted,
        volume,
        playbackRate,
        onReady,
        onPlay,
        onPause,
        onWaiting,
        onPlaying,
        onError,
        onEnded,
        onDurationChange,
      },
      ref,
    ) => (
      <Ui.PlayerWrapper>
        <ReactPlayer
          ref={ref}
          src={src}
          playing={playing}
          muted={muted}
          volume={volume}
          playbackRate={playbackRate}
          controls={false}
          width="100%"
          height="100%"
          playsInline
          onReady={onReady}
          onPlay={onPlay}
          onPause={onPause}
          onWaiting={onWaiting}
          onPlaying={onPlaying}
          onError={onError}
          onEnded={onEnded}
          onDurationChange={onDurationChange}
        />
      </Ui.PlayerWrapper>
    ),
  ),
);

const SeekBar = React.memo(({ seekBarRef, bufferedRef, progressRef, thumbRef, onMouseDown }) => (
  <Ui.SeekRow>
    <Ui.SeekBar ref={seekBarRef} onMouseDown={onMouseDown}>
      <Ui.SeekTrack />
      <Ui.SeekBuffered ref={bufferedRef} style={ZERO_WIDTH_STYLE} />
      <Ui.SeekProgress ref={progressRef} style={ZERO_WIDTH_STYLE} />
      <Ui.SeekThumb ref={thumbRef} style={ZERO_LEFT_STYLE}>
        <Ui.ThumbImage src={ramblaIcon} alt="" />
      </Ui.SeekThumb>
    </Ui.SeekBar>
  </Ui.SeekRow>
));

const formatTime = (sec) => {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const total = Math.floor(sec);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

const VideoPlayer = ({
  src,
  autoPlay = false,
  aspectRatio = '16 / 9',
  borderRadius = '8px',
  externalUrl,
  topOverlay,
  topOverlayPadding = '16px',
  onError,
  errorMessage = 'Unable to load video',
}) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const seekBarRef = useRef(null);
  const hideTimerRef = useRef(null);
  const rafRef = useRef(null);
  const seekingRef = useRef(false);

  const currentTimeRef = useRef(0);
  const bufferedRef = useRef(0);
  const durationRef = useRef(0);

  const progressElRef = useRef(null);
  const bufferedElRef = useRef(null);
  const thumbElRef = useRef(null);
  const timeElRef = useRef(null);

  const showControlsRef = useRef(initialState.showControls);

  const [state, dispatch] = useReducer(videoReducer, {
    ...initialState,
    playing: autoPlay,
  });

  const {
    playing,
    muted,
    volume,
    playbackRate,
    isFullscreen,
    isLoading,
    hasError,
    showControls,
    showRateMenu,
    hasStarted,
  } = state;

  const paintProgress = useCallback(() => {
    const d = durationRef.current;
    const t = currentTimeRef.current;
    const b = bufferedRef.current;
    const pct = d > 0 ? Math.min(100, (t / d) * 100) : 0;
    const bPct = d > 0 ? Math.min(100, (b / d) * 100) : 0;
    if (progressElRef.current) progressElRef.current.style.width = `${pct}%`;
    if (thumbElRef.current) thumbElRef.current.style.left = `${pct}%`;
    if (bufferedElRef.current) bufferedElRef.current.style.width = `${bPct}%`;
    if (timeElRef.current) {
      timeElRef.current.textContent = `${formatTime(t)} / ${formatTime(d)}`;
    }
  }, []);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    clearHideTimer();
    hideTimerRef.current = window.setTimeout(() => {
      if (seekingRef.current) return;
      dispatch({ type: 'SHOW_CONTROLS', payload: false });
    }, HIDE_CONTROLS_DELAY);
  }, [clearHideTimer]);

  useEffect(() => {
    showControlsRef.current = showControls;
  }, [showControls]);

  const revealControls = useCallback(() => {
    scheduleHide();
    if (showControlsRef.current) return;
    dispatch({ type: 'SHOW_CONTROLS', payload: true });
  }, [scheduleHide]);

  useEffect(
    () => () => {
      clearHideTimer();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    },
    [clearHideTimer],
  );

  useEffect(() => {
    currentTimeRef.current = 0;
    bufferedRef.current = 0;
    durationRef.current = 0;
    paintProgress();
    dispatch({ type: 'RESET_VIDEO' });
  }, [src, paintProgress]);

  useEffect(() => {
    if (!playing) return undefined;
    const tick = () => {
      const player = playerRef.current;
      if (player && !seekingRef.current) {
        if (Number.isFinite(player.currentTime)) {
          currentTimeRef.current = player.currentTime;
        }
        if (player.buffered && player.buffered.length > 0) {
          bufferedRef.current = player.buffered.end(player.buffered.length - 1);
        }
        if (Number.isFinite(player.duration) && player.duration > 0) {
          durationRef.current = player.duration;
        }
        paintProgress();
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [playing, paintProgress]);

  useEffect(() => {
    const handler = () =>
      dispatch({ type: 'SET_FULLSCREEN', payload: Boolean(document.fullscreenElement) });
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const togglePlay = useCallback(() => {
    if (hasError) return;
    dispatch({ type: 'TOGGLE_PLAY' });
    revealControls();
  }, [hasError, revealControls]);

  const toggleMute = useCallback(() => {
    dispatch({ type: 'TOGGLE_MUTED' });
    revealControls();
  }, [revealControls]);

  const handleVolumeChange = useCallback(
    (e) => {
      dispatch({ type: 'SET_VOLUME', payload: Number(e.target.value) });
      revealControls();
    },
    [revealControls],
  );

  const handleRateChange = useCallback(
    (rate) => {
      dispatch({ type: 'SET_RATE', payload: rate });
      revealControls();
    },
    [revealControls],
  );

  const seekTo = useCallback(
    (time) => {
      const player = playerRef.current;
      if (!player || !Number.isFinite(time)) return;
      const d = durationRef.current;
      const max = Number.isFinite(d) && d > 0 ? d : time;
      const safe = Math.max(0, Math.min(max, time));
      try {
        player.currentTime = safe;
      } catch (err) {
        // some providers may throw if not ready yet
      }
      currentTimeRef.current = safe;
      paintProgress();
    },
    [paintProgress],
  );

  const seekFromClientX = useCallback(
    (clientX) => {
      const bar = seekBarRef.current;
      const d = durationRef.current;
      if (!bar || !d) return;
      const rect = bar.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      seekTo(ratio * d);
    },
    [seekTo],
  );

  const handleSeekStart = useCallback(
    (e) => {
      e.preventDefault();
      seekingRef.current = true;
      clearHideTimer();
      seekFromClientX(e.clientX);

      const onMove = (ev) => seekFromClientX(ev.clientX);
      const onUp = () => {
        seekingRef.current = false;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        scheduleHide();
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [clearHideTimer, scheduleHide, seekFromClientX],
  );

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      containerRef.current.requestFullscreen?.();
    }
    revealControls();
  }, [revealControls]);

  const openExternalLink = useCallback(
    (event) => {
      event.stopPropagation();
      if (!externalUrl) return;
      window.open(externalUrl, '_blank', 'noopener,noreferrer');
      revealControls();
    },
    [externalUrl, revealControls],
  );

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    const handler = (e) => {
      const { target } = e;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return;
      switch (e.key) {
        case ' ':
        case 'k':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          seekTo((playerRef.current?.currentTime || 0) - SEEK_STEP);
          revealControls();
          break;
        case 'ArrowRight':
          e.preventDefault();
          seekTo((playerRef.current?.currentTime || 0) + SEEK_STEP);
          revealControls();
          break;
        case 'm':
        case 'M':
          toggleMute();
          break;
        case 'f':
        case 'F':
          toggleFullscreen();
          break;
        default:
          break;
      }
    };
    node.addEventListener('keydown', handler);
    return () => node.removeEventListener('keydown', handler);
  }, [togglePlay, toggleMute, toggleFullscreen, seekTo, revealControls]);

  useEffect(() => {
    if (!showRateMenu) return undefined;
    const handler = (e) => {
      if (e.target.closest('[data-rate-menu]')) return;
      dispatch({ type: 'HIDE_RATE_MENU' });
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showRateMenu]);

  const volumeFillPct = (muted ? 0 : volume) * 100;

  const handleReady = useCallback(() => dispatch({ type: 'SET_LOADING', payload: false }), []);
  const handlePlay = useCallback(() => dispatch({ type: 'PLAY' }), []);
  const handlePause = useCallback(() => dispatch({ type: 'PAUSE' }), []);
  const handleWaiting = useCallback(() => dispatch({ type: 'SET_LOADING', payload: true }), []);
  const handlePlaying = useCallback(() => dispatch({ type: 'SET_LOADING', payload: false }), []);
  const handleError = useCallback(
    (event) => {
      dispatch({ type: 'SET_ERROR' });
      onError?.(event);
    },
    [onError],
  );
  const handleEnded = useCallback(() => dispatch({ type: 'PAUSE' }), []);

  const handleDurationChange = useCallback(
    (e) => {
      const next = e?.target?.duration ?? playerRef.current?.duration ?? 0;
      if (Number.isFinite(next) && next > 0) {
        durationRef.current = next;
        paintProgress();
      }
    },
    [paintProgress],
  );

  const handleMouseLeave = useCallback(() => {
    if (playing) dispatch({ type: 'SHOW_CONTROLS', payload: false });
  }, [playing]);

  const handleToggleRateMenu = useCallback(() => dispatch({ type: 'TOGGLE_RATE_MENU' }), []);
  const overlaysVisible = !hasError && (showControls || !playing);

  return (
    <Ui.Container
      ref={containerRef}
      tabIndex={0}
      $aspectRatio={aspectRatio}
      $borderRadius={borderRadius}
      onMouseMove={revealControls}
      onMouseLeave={handleMouseLeave}
    >
      <MediaSurface
        ref={playerRef}
        src={src}
        playing={playing}
        muted={muted}
        volume={volume}
        playbackRate={playbackRate}
        onReady={handleReady}
        onPlay={handlePlay}
        onPause={handlePause}
        onWaiting={handleWaiting}
        onPlaying={handlePlaying}
        onError={handleError}
        onEnded={handleEnded}
        onDurationChange={handleDurationChange}
      />

      <Ui.ClickShield
        onClick={togglePlay}
        aria-label={playing ? 'Pause' : 'Play'}
        disabled={hasError}
      />

      {isLoading && !hasError && (
        <Ui.LoaderOverlay>
          <Ui.LoaderInner>
            <Ui.Loader src={ramblaIcon} alt="" />
          </Ui.LoaderInner>
        </Ui.LoaderOverlay>
      )}

      {hasError && (
        <Ui.ErrorOverlay role="alert">
          <Ui.ErrorMessage>{errorMessage}</Ui.ErrorMessage>
        </Ui.ErrorOverlay>
      )}

      {topOverlay && (
        <Ui.TopOverlay $visible={overlaysVisible} $padding={topOverlayPadding}>
          {topOverlay}
        </Ui.TopOverlay>
      )}

      <Ui.CenterPlayHint $visible={!hasStarted && !isLoading && !playing && !hasError}>
        <Ui.CenterPlayCircle>
          <MdPlayArrow />
        </Ui.CenterPlayCircle>
      </Ui.CenterPlayHint>

      <Ui.ControlsOverlay $visible={overlaysVisible}>
        <SeekBar
          seekBarRef={seekBarRef}
          bufferedRef={bufferedElRef}
          progressRef={progressElRef}
          thumbRef={thumbElRef}
          onMouseDown={handleSeekStart}
        />
        <Ui.ControlsRow>
          <Ui.ControlGroup>
            <Ui.ControlButton onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
              {playing ? <MdPause /> : <MdPlayArrow />}
            </Ui.ControlButton>
            <Ui.VolumeGroup>
              <Ui.ControlButton onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'}>
                {muted || volume === 0 ? <MdVolumeOff /> : <MdVolumeUp />}
              </Ui.ControlButton>
              <Ui.VolumeSlider
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={muted ? 0 : volume}
                onChange={handleVolumeChange}
                $fillPct={volumeFillPct}
                aria-label="Volume"
              />
            </Ui.VolumeGroup>
            <Ui.TimeDisplay ref={timeElRef}>0:00 / 0:00</Ui.TimeDisplay>
          </Ui.ControlGroup>
          <Ui.ControlGroup>
            <Ui.RateGroup data-rate-menu>
              <Ui.RateButton
                onClick={handleToggleRateMenu}
                aria-label="Playback speed"
                aria-expanded={showRateMenu}
              >
                {playbackRate}x
              </Ui.RateButton>
              {showRateMenu && (
                <Ui.RateMenu>
                  {PLAYBACK_RATES.map((rate) => (
                    <Ui.RateOption
                      key={rate}
                      $active={rate === playbackRate}
                      onClick={() => handleRateChange(rate)}
                    >
                      {rate}x
                    </Ui.RateOption>
                  ))}
                </Ui.RateMenu>
              )}
            </Ui.RateGroup>
            {externalUrl && (
              <Ui.ControlButton onClick={openExternalLink} aria-label="Open external link">
                <FiExternalLink size={18} />
              </Ui.ControlButton>
            )}
            <Ui.ControlButton
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? <MdFullscreenExit /> : <MdFullscreen />}
            </Ui.ControlButton>
          </Ui.ControlGroup>
        </Ui.ControlsRow>
      </Ui.ControlsOverlay>
    </Ui.Container>
  );
};

VideoPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  autoPlay: PropTypes.bool,
  aspectRatio: PropTypes.string,
  borderRadius: PropTypes.string,
  externalUrl: PropTypes.string,
  topOverlay: PropTypes.node,
  topOverlayPadding: PropTypes.string,
  onError: PropTypes.func,
  errorMessage: PropTypes.string,
};

export default VideoPlayer;
